import { IconSearch, IconX } from '@tabler/icons-react';
import { type FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Banner, Dropdown, Spinner } from '@/components/common';
import { useResource } from '@/hooks';
import { errorMessage } from '@/lib/api-client';
import type { ConsentTexts, Patient } from '@/types/api';
import { missingProtocolFields } from '@/utils/patient';
import { formatDate } from '../../chart/components';
import { patientsApi } from '../../patients/api';
import { type ConsentInput, consentsApi } from '../api';
import { patientFullName } from '../patientLines';

const TEXT_KEYS = ['purpose', 'risks', 'alternatives', 'consequences'] as const;
const EMPTY_TEXTS: ConsentTexts = { purpose: '', risks: '', alternatives: '', consequences: '' };

/** Age in whole years on the device's today, from YYYY-MM-DD. */
function ageOf(birthDate: string | null): number | null {
  if (!birthDate) return null;
  const [y, m, d] = birthDate.split('-').map(Number);
  const now = new Date();
  const had = now.getMonth() + 1 > m || (now.getMonth() + 1 === m && now.getDate() >= d);
  return now.getFullYear() - y - (had ? 0 : 1);
}

/** Search the patient list and pick one; a consent always belongs to a recorded patient. */
function PatientPicker({
  value,
  onChange,
}: {
  value: Patient | null;
  onChange: (p: Patient | null) => void;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [found, setFound] = useState<Patient[]>([]);
  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setFound([]);
      return;
    }
    let live = true;
    const timer = setTimeout(() => {
      patientsApi
        .list(q, 8)
        .then((list) => live && setFound(list))
        .catch(() => live && setFound([]));
    }, 200);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [query]);

  if (value) {
    return (
      <div className="consent-patient">
        <div className="grow">
          <strong>{patientFullName(value)}</strong>
          <span className="small muted" style={{ display: 'block' }}>
            {[value.birthDate && formatDate(`${value.birthDate}T12:00:00`), value.jmbg ?? value.lbo]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </div>
        <button
          type="button"
          className="icon-btn icon-btn--sm"
          onClick={() => onChange(null)}
          aria-label={t('consents.changePatient')}
          title={t('consents.changePatient')}
        >
          <IconX size={16} />
        </button>
      </div>
    );
  }
  return (
    <div className="stack">
      <div className="search">
        <IconSearch size={20} />
        <input
          className="input"
          type="search"
          // biome-ignore lint/a11y/noAutofocus: picking the patient is the first step of the form
          autoFocus
          placeholder={t('patients.searchPlaceholder')}
          aria-label={t('consents.patient')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      {found.length > 0 && (
        <ul className="list">
          {found.map((p) => (
            <li key={p.id}>
              <button type="button" className="list-item" onClick={() => onChange(p)}>
                <span className="grow">
                  <strong>
                    {p.lastName} {p.firstName}
                  </strong>
                  {p.birthDate && <span className="small muted"> · {p.birthDate.slice(0, 4)}</span>}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Who signs for a child under 15 or a person without legal capacity (čl. 19). */
function RepresentativeField(p: {
  value: string;
  onChange: (v: string) => void;
  age: number | null;
}) {
  const { t } = useTranslation();
  const child = p.age !== null && p.age < 15;
  return (
    <div className="field">
      <label htmlFor="c-rep">{t('consents.representative')}</label>
      <input
        id="c-rep"
        className="input"
        value={p.value}
        onChange={(e) => p.onChange(e.target.value)}
      />
      <span className={`small ${child && !p.value.trim() ? 'field__error' : 'muted'}`}>
        {child ? t('consents.childHint', { age: p.age }) : t('consents.representativeHint')}
      </span>
    </div>
  );
}

/** The four information texts, prefilled from the method. */
function TextFields({
  value,
  onChange,
}: {
  value: ConsentTexts;
  onChange: (update: (v: ConsentTexts) => ConsentTexts) => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      {TEXT_KEYS.map((key) => (
        <div className="field" key={key}>
          <label htmlFor={`c-${key}`}>{t(`consents.paper.${key}`)}</label>
          <textarea
            id={`c-${key}`}
            className="textarea"
            rows={4}
            maxLength={4000}
            value={value[key]}
            onChange={(e) => onChange((x) => ({ ...x, [key]: e.target.value }))}
          />
        </div>
      ))}
    </>
  );
}

type Props = {
  /** Preselected patient (opened from the chart). */
  patientId?: string;
  busy: boolean;
  onSave: (input: ConsentInput) => void;
  onCancel: () => void;
};

/**
 * A new consent: the patient, the invasive method (its texts prefilled from
 * the clinic's list and editable for this patient), the tooth or region, and
 * the legal representative when a child or a person without capacity consents.
 */
export function ConsentEditor({ patientId, busy, onSave, onCancel }: Props) {
  const { t } = useTranslation();
  const methods = useResource(() => consentsApi.methods(), 'methods');
  const preselected = useResource<Patient | null>(
    () => (patientId ? patientsApi.get(patientId) : Promise.resolve(null)),
    patientId ?? ''
  );
  const [patient, setPatient] = useState<Patient | null>(null);
  const [methodId, setMethodId] = useState('');
  const [methodName, setMethodName] = useState('');
  const [texts, setTexts] = useState<ConsentTexts>(EMPTY_TEXTS);
  const [site, setSite] = useState('');
  const [representative, setRepresentative] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (preselected.data) setPatient(preselected.data);
  }, [preselected.data]);

  const pickMethod = (id: string) => {
    const m = methods.data?.find((x) => x.id === id);
    setMethodId(id);
    setMethodName(m?.name ?? '');
    setTexts(
      m
        ? {
            purpose: m.purpose,
            risks: m.risks,
            alternatives: m.alternatives,
            consequences: m.consequences,
          }
        : EMPTY_TEXTS
    );
  };

  const missing = patient ? missingProtocolFields(patient) : [];
  const age = ageOf(patient?.birthDate ?? null);
  // Under 15 the legal representative consents (čl. 19).
  const signer = !(age !== null && age < 15) || representative.trim().length > 0;
  const ready =
    !!patient &&
    signer &&
    !!methodName.trim() &&
    TEXT_KEYS.every((k) => texts[k].trim().length > 0);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!(ready && patient)) return;
    onSave({
      patientId: patient.id,
      methodId: methodId || null,
      methodName: methodName.trim(),
      purpose: texts.purpose.trim(),
      risks: texts.risks.trim(),
      alternatives: texts.alternatives.trim(),
      consequences: texts.consequences.trim(),
      site: site.trim() || null,
      representativeName: representative.trim() || null,
      note: note.trim() || null,
    });
  };

  if (methods.error) return <Banner tone="error">{errorMessage(methods.error)}</Banner>;
  if (!methods.data || (patientId && preselected.loading)) return <Spinner />;

  return (
    <form className="stack consent-editor" onSubmit={submit}>
      <section className="card stack">
        <span className="field__label">{t('consents.patient')}</span>
        <PatientPicker value={patient} onChange={setPatient} />
        {missing.length > 0 && (
          <p className="small muted">
            {t('consents.missingDetails', {
              fields: missing.map((f) => t(`patients.form.${f}`)).join(', '),
            })}
          </p>
        )}
      </section>

      <section className="card stack">
        <div className="field">
          <label htmlFor="c-method">{t('consents.method')}</label>
          <Dropdown
            id="c-method"
            value={methodId}
            options={[
              { value: '', label: t('consents.pickMethod') },
              ...methods.data.map((m) => ({ value: m.id, label: m.name })),
            ]}
            onChange={pickMethod}
          />
        </div>
        {methodId === '' && (
          <div className="field">
            <label htmlFor="c-method-name">{t('consents.methodName')}</label>
            <input
              id="c-method-name"
              className="input"
              value={methodName}
              onChange={(e) => setMethodName(e.target.value)}
            />
          </div>
        )}
        <div className="field">
          <label htmlFor="c-site">{t('consents.site')}</label>
          <input
            id="c-site"
            className="input"
            placeholder={t('consents.sitePlaceholder')}
            value={site}
            onChange={(e) => setSite(e.target.value)}
          />
        </div>
        <RepresentativeField value={representative} onChange={setRepresentative} age={age} />
      </section>

      {(methodId || methodName) && (
        <section className="card stack">
          <p className="small muted">{t('consents.textsHint')}</p>
          <TextFields value={texts} onChange={setTexts} />
          <div className="field">
            <label htmlFor="c-note">{t('consents.paper.note')}</label>
            <textarea
              id="c-note"
              className="textarea"
              maxLength={2000}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
        </section>
      )}

      <div className="row">
        <button type="submit" className="btn btn--dark" disabled={busy || !ready}>
          {t('consents.issue')}
        </button>
        <button type="button" className="btn" onClick={onCancel} disabled={busy}>
          {t('common.cancel')}
        </button>
      </div>
    </form>
  );
}
